import { Calendar, CheckCircle, Clock, TrendingUp } from "lucide-react";
import { DateTime } from "luxon";
import { timezoneUtils } from "@/lib/timezone";
import { useWorkerScheduleStats } from "../hooks";

export function WorkerScheduleHeader() {
  const stats = useWorkerScheduleStats();

  const currentTime = DateTime.fromJSDate(new Date());
  const currentTimeString = currentTime.toFormat("HH:mm");
  const _todayDate = currentTime.toFormat("EEEE, MMMM d");

  return (
    <div className="bg-card border border-border rounded-lg px-3 sm:px-4 pt-3 sm:pt-4 pb-0 mb-4">
      {/* Time and Stats as Compact Horizontal List */}
      <div className="flex items-center justify-between gap-2 sm:gap-4 mb-3 sm:mb-4">
        {/* Current Time - Vertical Stack */}
        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
          <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
          <div className="flex flex-col text-center min-w-0">
            <div className="text-sm sm:text-lg font-bold text-foreground">{currentTimeString}</div>
            <p className="text-xs text-muted-foreground truncate">
              {currentTime.toFormat("MMM d")}
            </p>
          </div>
        </div>

        {/* Stats as very compact horizontal items */}
        <div className="flex items-center gap-2 sm:gap-6">
          <div className="flex flex-col items-center gap-0.5 sm:gap-1">
            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground" />
            <div className="text-center">
              <div className="text-xs sm:text-lg font-bold text-foreground">
                {stats.totalAppointments}
              </div>
              <p className="text-xs text-muted-foreground hidden sm:block">Today</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-0.5 sm:gap-1">
            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
            <div className="text-center">
              <div className="text-xs sm:text-lg font-bold text-green-600">
                {stats.completedAppointments}
              </div>
              <p className="text-xs text-muted-foreground hidden sm:block">Done</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-0.5 sm:gap-1">
            <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
            <div className="text-center">
              <div className="text-xs sm:text-lg font-bold text-blue-600">
                {stats.upcomingAppointments}
              </div>
              <p className="text-xs text-muted-foreground hidden sm:block">Next</p>
            </div>
          </div>
        </div>
      </div>

      {/* Current and Next Appointment */}
      {stats.currentAppointment && (
        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
          <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300 font-medium mb-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
            <span className="text-xs sm:text-sm">Current</span>
          </div>
          <div className="text-xs sm:text-sm">
            <p className="font-semibold">
              {stats.currentAppointment.customer?.firstName}{" "}
              {stats.currentAppointment.customer?.lastName}
            </p>
            <p className="text-blue-600 dark:text-blue-400">
              {timezoneUtils.fromISO(stats.currentAppointment.startTime).toFormat("HH:mm")} -
              {timezoneUtils.fromISO(stats.currentAppointment.endTime).toFormat("HH:mm")}
            </p>
          </div>
        </div>
      )}

      {stats.nextAppointment && !stats.currentAppointment && (
        <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
          <div className="flex items-center gap-2 text-green-700 dark:text-green-300 font-medium mb-1">
            <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="text-xs sm:text-sm">Next Up</span>
          </div>
          <div className="text-xs sm:text-sm">
            <p className="font-semibold">
              {stats.nextAppointment.customer?.firstName} {stats.nextAppointment.customer?.lastName}
            </p>
            <p className="text-green-600 dark:text-green-400">
              {timezoneUtils.fromISO(stats.nextAppointment.startTime).toFormat("HH:mm")} -
              {timezoneUtils.fromISO(stats.nextAppointment.endTime).toFormat("HH:mm")}
            </p>
          </div>
        </div>
      )}

      {/* Remove bottom padding with conditional pb only when appointment cards exist */}
      {(stats.currentAppointment || stats.nextAppointment) && <div className="pb-3 sm:pb-4" />}
    </div>
  );
}
